<ng-template #alertFilterTemplate>
  <!-- Severity Summary Cards -->
  <div class="severity-summary-row" *ngIf="!hideSeverityFilter && !filterDrawerEnabled">
    <div *ngFor="let sev of displaySeverities"
         class="severity-summary-card"
         [class.selected]="selectedAlertSeverities.includes(sev.value)"
         (click)="toggleSeverity(sev.value)"
         (keydown.enter)="toggleSeverity(sev.value)"
         (keydown.space)="toggleSeverity(sev.value); $event.preventDefault()"
         tabindex="0"
         role="button"
         [attr.aria-pressed]="selectedAlertSeverities.includes(sev.value)">
      <div class="severity-summary-label" mat-subheader>{{ sev.label | translate }}</div>
      <div class="severity-summary-count" [style.color]="severityColorMap[sev.value]">
        <ng-container *ngIf="!loadingSummary">
          <ng-container *ngIf="!isFiltered() || sev.count === sev.baselineCount">{{ sev.count | number }}</ng-container>
          <ng-container *ngIf="isFiltered() && sev.count !== sev.baselineCount">{{ sev.count | number }}<span class="baseline-count"> / {{ sev.baselineCount | number }}</span></ng-container>
        </ng-container>
        <span *ngIf="loadingSummary" class="count-skeleton">&mdash;</span>
      </div>
      <div class="severity-summary-desc" mat-line>{{ severityDescMap[sev.value] | translate }}</div>
    </div>
  </div>

  <!-- Filter Bar -->
  <div class="filter-bar" *ngIf="!filterDrawerEnabled">
    <!-- Row 1: Status chips -->
    <div class="filter-bar-row" *ngIf="hasCaseCreationEnabled">
      <span class="filter-group-label" mat-subheader>{{ '@caseStatus' | translate }}</span>
      <ng-container *ngTemplateOutlet="alertCaseStatus"></ng-container>
    </div>

    <!-- Row 2: Search inputs + actions -->
    <div class="filter-bar-row" [formGroup]="searchForm">
      <input class="fhd-filter-input filter-field" type="text" autocomplete="off"
        formControlName="nameOrVinSearch" [placeholder]="'@assetNameOrVin' | translate"
        (keydown)="handleSearchKeydown($event)">

      <ng-container *ngTemplateOutlet="alertSeverityFilter"></ng-container>
      <ng-container *ngTemplateOutlet="alertLocationFilters"></ng-container>
      <ng-container *ngTemplateOutlet="alertRuleDateFilters"></ng-container>
      <ng-container *ngTemplateOutlet="alertSavedFilters"></ng-container>

      <div class="filter-actions">
        <button class="more-filters-btn" *ngIf="hasMoreFilters"
                (click)="showMoreFilters = !showMoreFilters"
                [class.open]="showMoreFilters"
                [class.has-active]="hasMoreFiltersActive">
          <mat-icon>tune</mat-icon>
          <span>{{ '@moreFilters' | translate }}</span>
          <span class="more-filters-dot" *ngIf="hasMoreFiltersActive"></span>
        </button>

        <button class="fhd-clear-filters-btn" *ngIf="isFiltered()" (click)="clearAllFilters()">
          <mat-icon>filter_list_off</mat-icon>
          {{ '@clearAllFilters' | translate }}
        </button>
      </div>

    </div>
  </div>

  <!-- More Filters Panel -->
  <div class="more-filters-panel" *ngIf="showMoreFilters && hasMoreFilters && !filterDrawerEnabled">
    <div class="filter-region">
      <span class="filter-region-label">{{ '@caseFilters' | translate }}</span>
      <div class="more-filters-grid">
      <ng-container *ngTemplateOutlet="alertCaseFilters"></ng-container>
      </div>
    </div>
  </div>

  <!-- VIN fixed filter (when nested) -->
  <div class="vin-filter-display" *ngIf="vin && isNested">
    <mat-icon>directions_car</mat-icon>
    <span>{{ '@vin' | translate }}: {{ filterVin }}</span>
  </div>

</ng-template>

<!-- Grouped Alerts: one minimal cell combining the "+N" count and the expand
     chevron. Toggles the group's child rows; stopPropagation keeps row-click navigation. -->
<ng-template #groupExpandCell let-row="row">
  <div class="group-expand-cell" *ngIf="(row.count ?? 1) > 1"
       (click)="toggleGroupExpand(row); $event.stopPropagation()"
       [title]="(row.expanded ? '@collapse' : '@expand') | translate">
    <span class="group-expand-count">{{ row.groupCountLabel }}</span>
    <mat-icon>{{ row.expanded ? 'expand_less' : 'expand_more' }}</mat-icon>
  </div>
</ng-template>

<ng-template #alertSavedFilters>
  <app-saved-filters *ngIf="savedFiltersEnabled" area="alerts" [readQuery]="readSavedQuery" [query]="savedQuery" [hasFilters]="isFiltered()" [alwaysShowSave]="filterDrawerEnabled" (apply)="applySavedFilter($event)"></app-saved-filters>
</ng-template>

<ng-template #alertSeverityFilter>
  <app-filter-dropdown class="filter-field" *ngIf="hideSeverityFilter"
    [multiple]="true"
    [label]="'@severity' | translate"
    [options]="alertSeverityOptions"
    [ngModel]="selectedAlertSeverities"
    [ngModelOptions]="{ standalone: true }"
    (ngModelChange)="filterByAlertSeverity($event)">
  </app-filter-dropdown>
</ng-template>

<ng-template #alertLocationFilters>
  <app-filter-dropdown class="filter-field"
    [multiple]="true"
    [searchable]="true"
    [label]="'@homeLocations' | translate"
    [options]="homeLocationOptions"
    [ngModel]="selectedLocationGroupIds"
    [ngModelOptions]="{ standalone: true }"
    (ngModelChange)="filterByLocationGroup($event)">
  </app-filter-dropdown>

  <app-filter-dropdown class="filter-field"
    [multiple]="true"
    [searchable]="true"
    [label]="'@shopLocations' | translate"
    [options]="shopLocationOptions"
    [ngModel]="selectedShopLocationIds"
    [ngModelOptions]="{ standalone: true }"
    (ngModelChange)="filterByShopLocation($event)">
  </app-filter-dropdown>
</ng-template>

<ng-template #alertRuleDateFilters>
  <app-filter-dropdown class="filter-field"
    [multiple]="true"
    [searchable]="true"
    [label]="'@alertName' | translate"
    [options]="alertRuleOptions"
    [ngModel]="selectedAlertRuleIds"
    [ngModelOptions]="{ standalone: true }"
    (ngModelChange)="filterByAlertRule($event)">
  </app-filter-dropdown>

  <app-date-range-filter class="filter-field"
    [title]="'@createdDate' | translate"
    [ngModel]="createdRange"
    [ngModelOptions]="{ standalone: true }"
    (ngModelChange)="filterByCreatedDate($event)">
  </app-date-range-filter>
</ng-template>

<ng-template #alertCaseStatus>
  <ng-container *ngTemplateOutlet="caseActiveClosedPills"></ng-container>
  <span class="status-group-divider" aria-hidden="true"></span>
  <ng-container *ngFor="let status of caseStatuses">
    <ng-container *ngTemplateOutlet="caseStatusChip; context: { $implicit: status }"></ng-container>
  </ng-container>
</ng-template>

<!-- Drawer only: each status sits under the internal status it maps to (CaseConfiguration). -->
<ng-template #alertCaseStatusGrouped>
  <div class="drawer-case-status">
    <ng-container *ngTemplateOutlet="caseActiveClosedPills"></ng-container>
    <ng-container *ngIf="caseStatuses[0] as notStarted">
      <ng-container *ngTemplateOutlet="caseStatusChip; context: { $implicit: notStarted }"></ng-container>
    </ng-container>
    <ng-container *ngFor="let status of caseStatusLead">
      <ng-container *ngTemplateOutlet="caseStatusChip; context: { $implicit: status }"></ng-container>
    </ng-container>
  </div>
  <div class="drawer-case-group" *ngFor="let group of caseStatusGroups">
    <div class="drawer-case-head">
      <span class="fd-label">{{ group.labelKey | translate }}</span>
      <button type="button" class="drawer-case-select" [attr.aria-pressed]="isCaseGroupSelected(group.values)"
              (click)="toggleCaseGroup(group.values)">
        {{ (isCaseGroupSelected(group.values) ? '@clear' : '@selectAll') | translate }}
      </button>
    </div>
    <div class="drawer-case-status">
      <ng-container *ngFor="let status of group.statuses">
        <ng-container *ngTemplateOutlet="caseStatusChip; context: { $implicit: status }"></ng-container>
      </ng-container>
    </div>
  </div>
</ng-template>

<ng-template #caseActiveClosedPills>
  <span class="filter-pill">
    <button class="filter-chip"
            [class.active]="isActiveGroupSelected"
            (click)="toggleStatusGroup(activeStatusValues)"
            [attr.aria-pressed]="isActiveGroupSelected">
      {{ '@allActive' | translate }} <span class="chip-count">&middot; <ng-container *ngIf="!isFiltered() || activeGroupCount === activeGroupBaselineCount">{{ activeGroupCount | number }}</ng-container><ng-container *ngIf="isFiltered() && activeGroupCount !== activeGroupBaselineCount">{{ activeGroupCount | number }} / {{ activeGroupBaselineCount | number }}</ng-container></span>
    </button>
    <button class="filter-chip"
            [class.active]="isClosedGroupSelected"
            (click)="toggleStatusGroup(closedStatusValues)"
            [attr.aria-pressed]="isClosedGroupSelected">
      {{ '@allClosed' | translate }} <span class="chip-count">&middot; <ng-container *ngIf="!isFiltered() || closedGroupCount === closedGroupBaselineCount">{{ closedGroupCount | number }}</ng-container><ng-container *ngIf="isFiltered() && closedGroupCount !== closedGroupBaselineCount">{{ closedGroupCount | number }} / {{ closedGroupBaselineCount | number }}</ng-container></span>
    </button>
  </span>
</ng-template>

<ng-template #caseStatusChip let-status>
  <button class="filter-chip"
          [class.active]="selectedStatuses.includes(status.value)"
          (click)="toggleStatus(status.value)"
          [attr.aria-pressed]="selectedStatuses.includes(status.value)">
    {{ status.label | translate }} <span class="chip-count">&middot; <ng-container *ngIf="!isFiltered() || status.count === status.baselineCount">{{ status.count | number }}</ng-container><ng-container *ngIf="isFiltered() && status.count !== status.baselineCount">{{ status.count | number }} / {{ status.baselineCount | number }}</ng-container></span>
  </button>
</ng-template>

<ng-template #alertCaseFilters>
  <app-filter-dropdown class="filter-field" *ngIf="hasCaseCreationEnabled"
    [multiple]="true"
    [searchable]="true"
    [label]="'@assignedTo' | translate"
    [options]="assignedUserOptions"
    [ngModel]="selectedAssignedUserIds"
    [ngModelOptions]="{ standalone: true }"
    (ngModelChange)="filterByAssignedUser($event)">
  </app-filter-dropdown>
  <app-filter-dropdown class="filter-field" *ngIf="hasCaseCreationEnabled"
    [multiple]="true"
    [searchable]="true"
    [label]="'@lastUpdatedBy' | translate"
    [options]="lastUpdatedByOptions"
    [ngModel]="selectedLastUpdatedByUserIds"
    [ngModelOptions]="{ standalone: true }"
    (ngModelChange)="filterByLastUpdatedUser($event)">
  </app-filter-dropdown>
  <app-filter-dropdown class="filter-field" *ngIf="hasCaseCreationEnabled"
    [multiple]="true"
    [searchable]="true"
    [label]="'@assignedToLocation' | translate"
    [options]="assignedLocationOptions"
    [ngModel]="selectedAssignedLocationIds"
    [ngModelOptions]="{ standalone: true }"
    (ngModelChange)="filterByAssignedLocation($event)">
  </app-filter-dropdown>
</ng-template>

<app-dynamic-table title="{{ '@alerts' }}" [functions]="tableFunctions" (functionCallback)="processFunction($event)"
  [includeSelect]="includeSelect" [dataSource]="dataSource" [gridDisplayColumns]="gridDisplayColumns" [length]="length"
  [loading]="loading" [customFilterTemplate]="alertFilterTemplate" [hideAdvancedFilter]="filterTemplateEmpty"
  [hideAdvancedSearch]="true" [defaultShowFilters]="true" [projectedDrawer]="filterDrawerEnabled" pathName="/diagnostics/alert">
  <app-filter-drawer-toggle tableFilterActions class="fhd-title-filters" *ngIf="filterDrawerEnabled" [formGroup]="searchForm"
                            [open]="drawerOpen" [count]="drawerCounts.total" [canClear]="isFiltered()"
                            (toggle)="drawerOpen = !drawerOpen" (clearAll)="clearAllFilters()">
    <input class="fhd-filter-input" type="text" autocomplete="off"
      formControlName="nameOrVinSearch" [placeholder]="'@assetNameOrVin' | translate"
      (keydown)="handleSearchKeydown($event)">
  </app-filter-drawer-toggle>
  <app-filter-drawer tableDrawer *ngIf="filterDrawerEnabled" [open]="drawerOpen" [count]="drawerCounts.total"
                     [canClear]="isFiltered()" (clearAll)="clearAllFilters()" (closed)="drawerOpen = false">
    <ng-container ngProjectAs="[fdSaved]"><ng-container *ngTemplateOutlet="alertSavedFilters"></ng-container></ng-container>
    <app-filter-drawer-section [title]="'@alert' | translate" [count]="drawerCounts.alert">
      <div class="fd-chips" role="group" [attr.aria-label]="'@severity' | translate">
        <button type="button" class="fd-chip" *ngFor="let sev of displaySeverities"
                [class.active]="selectedAlertSeverities.includes(sev.value)"
                [attr.aria-pressed]="selectedAlertSeverities.includes(sev.value)"
                (click)="toggleSeverity(sev.value)">
          <span class="fd-swatch" [style.background]="'var(--sc-sev-' + sev.value.toLowerCase() + ')'" aria-hidden="true"></span>
          {{ sev.label | translate }}
        </button>
      </div>
      <ng-container *ngTemplateOutlet="alertRuleDateFilters"></ng-container>
    </app-filter-drawer-section>
    <app-filter-drawer-section *ngIf="hasMoreFilters" [title]="'@case' | translate" [count]="drawerCounts.case">
      <ng-container *ngTemplateOutlet="alertCaseStatusGrouped"></ng-container>
      <ng-container *ngTemplateOutlet="alertCaseFilters"></ng-container>
    </app-filter-drawer-section>
    <app-filter-drawer-section [title]="'@locations' | translate" [count]="drawerCounts.locations">
      <ng-container *ngTemplateOutlet="alertLocationFilters"></ng-container>
    </app-filter-drawer-section>
  </app-filter-drawer>
</app-dynamic-table>
